import React, { useRef } from "react";
import { ArrowUpTrayIcon, DocumentIcon } from "@heroicons/react/24/outline";
import { cn } from "../../lib/utils";
import { useDragAndDrop } from "../../hooks/useDragAndDrop";
import { toast } from "react-hot-toast";

interface DragDropFileUploadProps {
  onFilesSelect: (files: File[]) => void;
  selectedFile?: File | null;
  acceptedFileTypes?: string[];
  maxFileSize?: number; // in bytes
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  showFileInfo?: boolean;
}

export default function DragDropFileUpload({
  onFilesSelect,
  selectedFile,
  acceptedFileTypes,
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  multiple = false,
  disabled = false,
  className = "",
  placeholder = "Glissez-déposez un fichier ici ou cliquez pour sélectionner",
  showFileInfo = true,
}: DragDropFileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileDrop = (files: FileList) => {
    const { validFiles, errors } = validateFiles(files);

    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
      return;
    }

    if (validFiles.length > 0) {
      onFilesSelect(validFiles);
    }
  };

  const { isDragOver, isDragActive, dragHandlers, validateFiles } =
    useDragAndDrop({
      onFileDrop: handleFileDrop,
      acceptedFileTypes,
      maxFileSize,
      multiple,
    });

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileDrop(files);
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getAcceptString = () => {
    if (!acceptedFileTypes || acceptedFileTypes.length === 0) return undefined;
    return acceptedFileTypes.join(",");
  };

  return (
    <div className={cn("relative", className)}>
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled}
        accept={getAcceptString()}
        multiple={multiple}
      />

      <div
        {...dragHandlers}
        onClick={handleClick}
        className={cn(
          "border-2 border-dashed rounded-lg p-6 cursor-pointer transition-all duration-200",
          "flex flex-col items-center justify-center gap-3",
          "bg-card hover:bg-card/60",
          {
            "border-border text-secondary":
              !isDragActive && !isDragOver && !className.includes("border-red"),
            "border-accent bg-accent/10 text-accent":
              isDragActive || isDragOver,
            "opacity-50 cursor-not-allowed": disabled,
            "border-accent/50 bg-accent/5":
              selectedFile &&
              !isDragActive &&
              !className.includes("border-red"),
          },
          className
        )}
      >
        {selectedFile ? (
          <div className="flex flex-col items-center gap-2 w-full">
            <DocumentIcon className="w-8 h-8 text-accent" />
            <div className="text-center">
              <div className="text-sm font-medium text-primary truncate max-w-full">
                {selectedFile.name}
              </div>
              {showFileInfo && (
                <div className="text-xs text-secondary mt-1">
                  {formatFileSize(selectedFile.size)}
                </div>
              )}
            </div>
            <div className="text-xs text-secondary text-center">
              Cliquez ou glissez-déposez pour remplacer
            </div>
          </div>
        ) : (
          <>
            <ArrowUpTrayIcon
              className={cn(
                "w-8 h-8 transition-colors",
                isDragActive || isDragOver ? "text-accent" : "text-secondary"
              )}
            />
            <div className="text-center">
              <div
                className={cn(
                  "text-sm font-medium transition-colors",
                  isDragActive || isDragOver ? "text-accent" : "text-primary"
                )}
              >
                {isDragActive ? "Déposez le fichier ici" : placeholder}
              </div>
              {showFileInfo && (
                <div className="text-xs text-secondary mt-1">
                  {acceptedFileTypes && acceptedFileTypes.length > 0 && (
                    <div>Types acceptés: {acceptedFileTypes.join(", ")}</div>
                  )}
                  {maxFileSize && (
                    <div>Taille max: {formatFileSize(maxFileSize)}</div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
